//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { makeStyles } from '@material-ui/core';
import SimpleBar from 'simplebar-react';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory, useParams } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../system/firebase';
import { PATH_DASHBOARD } from '../system/constants';
import { AppContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { getFormattedDate } from '../common/dateHelper';
import {
    BASE_URL,
    DASHBOARD_ID_CHANGE_PASSWORD,
    DASHBOARD_ID_MY_SETTINGS,
    DASHBOARD_ID_MY_SETTINGS_GENERAL,
    DASHBOARD_ID_MY_SETTINGS_LINK,
    DASHBOARD_ID_MY_SETTINGS_MY_ACCOUNT,
    DASHBOARD_MY_SETTINGS,
    PAGE_TITLE_JS_MY_SETTINGS,
} from '../common/constants';
import { getUserDateFormat } from '../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import Link from './subComponents/Link';
import General from './subComponents/General';
import Footer from '../dashboard/subComponents/Footer';
import ChangePassword from './subComponents/ChangePassword';
import MyAccount from './subComponents/MyAccount';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { /* pendingAccountSetup, */ isGeneralActive, isMyLinkActive, isChangePasswordActive, isMyAccountActive } = params;

    const navItemStyles = {
        padding: '10px 0',
        color: '#adadad',
        cursor: 'pointer',
        textAlign: 'left',
        // cursor: pendingAccountSetup ? 'default' : 'pointer',
        fontSize: '16px',
    };

    const navItemActiveStyles = {
        color: 'black',
        fontWeight: 'bold',
    };

    const getItemActiveStyles = isActive => {
        return isActive ? navItemActiveStyles : undefined;
    };

    return makeStyles(theme => ({
        root: {
            height: '100%',
            background: '#FAFAFC',
        },
        header: {
            height: '128px',
        },
        content: {
            flex: 1,
            overflowY: 'hidden',
            marginTop: 0,
            marginBottom: 0,
            borderRadius: 10,
            backgroundColor: '#fff',
        },
        settingsRight: {
            overflowY: 'auto',
            height: '100%',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },

        headerTitle: {
            marginTop: '38px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        headerToday: {
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        todayIcon: {
            fontSize: '16px',
            color: 'rgba(17, 30, 20, 0.4)',
        },
        todayContainer: {
            marginTop: '1px',
            marginLeft: '9px',
            fontSize: '13px',
            color: 'rgba(17, 30, 20, 0.6)',
        },
        title: {
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: '26px',
            lineHeight: '30px',
            color: '#111E14',
        },
        dateBox: {
            display: 'flex',
            alignItems: 'center',
        },
        calendarIcon: { color: 'rgba(17, 30, 20, 0.6)', fontSize: 14 },
        date: {
            color: 'rgba(17, 30, 20, 0.6)',
            marginLeft: '5px',
        },

        navList: {
            listStyle: 'none',
            padding: '40px 0 0 50px',
            marginTop: 0,
            marginBottom: 0,
            [theme.breakpoints.down('md')]: {
                padding: '10px 0 0 10px',
            },
            [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'space-around', padding: '10px 0 0 0px' },
        },

        settingsContainer: {
            padding: '40px 50px 40px 0',
            [theme.breakpoints.down('md')]: {
                padding: '10px 10px 55px 10px',
            },
            [theme.breakpoints.down('sm')]: {
                padding: '10px 10px 62px 10px',
            },
        },
        profileLeftDivider: {
            padding: '0px 10px',
            [theme.breakpoints.down('sm')]: { padding: '10px 10px 5px 10px' },
        },

        generalNavList: {
            ...navItemStyles,
            ...getItemActiveStyles(isGeneralActive),
            [theme.breakpoints.down('sm')]: {
                fontSize: '14pt',
            },
        },
        myLinkNavList: {
            ...navItemStyles,
            ...getItemActiveStyles(isMyLinkActive),
            // display: pendingAccountSetup ? 'none' : undefined,
            [theme.breakpoints.down('sm')]: {
                fontSize: '14pt',
            },
        },
        changePassword: {
            ...navItemStyles,
            ...getItemActiveStyles(isChangePasswordActive),
            // display: pendingAccountSetup ? 'none' : undefined,
            [theme.breakpoints.down('sm')]: {
                fontSize: '14pt',
            },
        },
        myAccountNavList: {
            ...navItemStyles,
            ...getItemActiveStyles(isMyAccountActive),
            // display: pendingAccountSetup ? 'none' : undefined,
            [theme.breakpoints.down('sm')]: {
                fontSize: '14pt',
            },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, history, userDateFormat } = params;

    // const isNotificationsActive = active === 'notifications';

    const defns = {
        root: { item: true, container: true, direction: 'column', lg: 12, md: 12, sm: 12, xs: 12 },
        content: { container: true, item: true, spacing: 0 },
        title: {},
        dateBox: {},
        date: { variant: 'body2' },
        calendarIcon: {},
        header: { item: true },

        // SIDE MENU
        settingsLeft: { item: true, lg: 3, md: 12, sm: 12, xs: 12 },
        navList: {},
        generalNavList: {
            onClick: () => history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_GENERAL}`),
        },
        myLinkNavList: {
            onClick: () => history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_LINK}`),
        },
        changePassword: {
            onClick: () => history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_CHANGE_PASSWORD}`),
        },
        myAccountNavList: {
            onClick: () => history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_MY_ACCOUNT}`),
        },

        // CONTENT
        settingsRight: { item: true, lg: 9, md: 12, sm: 12, xs: 12 },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const SUBPAGE_ID_GENERAL = 'general';
const SUBPAGE_ID_MY_URL = 'my-url';
const SUBPAGE_ID_CHANGE_PASSWORD = 'change-password';
const SUBPAGE_ID_MY_ACCOUNT = 'my-account';

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MySettings = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const userDateFormat = getUserDateFormat(appContext);

    const theme = useTheme();
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const { pendingAccountSetup, currentUser } = appContext;
    const authTypeEmail = _get(currentUser, 'authTypeEmail');

    const history = useHistory();
    const { subPageId } = useParams();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [activeSubPageId, setActiveSubPageId] = useState(SUBPAGE_ID_GENERAL);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_JS_MY_SETTINGS);
    }, []);

    useEffect(() => {
        switch (subPageId) {
            case DASHBOARD_ID_MY_SETTINGS_LINK:
                setActiveSubPageId(SUBPAGE_ID_MY_URL);
                break;
            case DASHBOARD_ID_CHANGE_PASSWORD:
                setActiveSubPageId(SUBPAGE_ID_CHANGE_PASSWORD);
                break;
            case DASHBOARD_ID_MY_SETTINGS_MY_ACCOUNT:
                setActiveSubPageId(SUBPAGE_ID_MY_ACCOUNT);
                break;
            default:
                setActiveSubPageId(SUBPAGE_ID_GENERAL);
        }
    }, [subPageId]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isGeneralActive = activeSubPageId === SUBPAGE_ID_GENERAL;
    const isMyLinkActive = activeSubPageId === SUBPAGE_ID_MY_URL;
    const isChangePasswordActive = activeSubPageId === SUBPAGE_ID_CHANGE_PASSWORD;
    const isMyAccountActive = activeSubPageId === SUBPAGE_ID_MY_ACCOUNT;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ pendingAccountSetup, isGeneralActive, isMyLinkActive, isChangePasswordActive, isMyAccountActive })();
    const defns = getDefns({ classes, history, userDateFormat });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Grid {...defns.root}>
            <AfitdPageHead title={PAGE_TITLE_JS_MY_SETTINGS} />
            <Grid {...defns.header}>
                <div {...defns.headerTitle}>
                    <div {...defns.title}>{DASHBOARD_MY_SETTINGS}</div>
                </div>
                <div {...defns.headerToday}>
                    <CalendarTodayIcon {...defns.todayIcon} />
                    <div {...defns.todayContainer}>
                        <AfitdTypography {...defns.todayText} />
                    </div>
                </div>
            </Grid>
            <Grid {...defns.content}>
                <Grid {...defns.settingsLeft}>
                    <ul {...defns.navList}>
                        <li {...defns.generalNavList}>General</li>
                        <li {...defns.myLinkNavList}>My URL</li>
                        {authTypeEmail && <li {...defns.changePassword}>Change Password</li>}
                        <li {...defns.myAccountNavList}>My Account</li>
                    </ul>
                    {mdMatches ? (
                        <Grid {...defns.profileLeftDivider}>
                            <Divider />
                        </Grid>
                    ) : null}
                </Grid>
                <Grid {...defns.settingsRight}>
                    <SimpleBar style={{ height: '100%' }}>
                        <div {...defns.settingsContainer}>
                            {activeSubPageId === SUBPAGE_ID_GENERAL && <General />}
                            {activeSubPageId === SUBPAGE_ID_MY_URL && <Link />}
                            {activeSubPageId === SUBPAGE_ID_CHANGE_PASSWORD && authTypeEmail && <ChangePassword />}
                            {activeSubPageId === SUBPAGE_ID_MY_ACCOUNT && <MyAccount />}
                        </div>
                    </SimpleBar>
                </Grid>
            </Grid>
            <Grid {...defns.bottomFooter}>{mdMatches ? <Footer /> : null}</Grid>
        </Grid>
    );
};

export default MySettings;
